/**
 * Helpers + form controls common cho mọi properties panel.
 */
import * as React from "react";

import type { FormFieldSchema } from "@/api/forms";
import { Input } from "@/components/ui/input";

export interface PropsEditorProps {
  field: FormFieldSchema;
  onUpdate: (patch: Partial<FormFieldSchema>) => void;
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase font-semibold text-muted-foreground border-b pb-1">
        {title}
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

export function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium mb-0.5">{label}</label>
      {children}
      {hint ? <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p> : null}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 text-sm"
    />
  );
}

export function NumberInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <Input
      type="number"
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : Number(v));
      }}
      placeholder={placeholder}
      className="h-8 text-sm"
    />
  );
}

export function CheckboxInput({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5"
      />
      {label}
    </label>
  );
}

export function CommonProps({ field, onUpdate }: PropsEditorProps): JSX.Element {
  return (
    <Section title="Cơ bản">
      <Row
        label="Tên field (snake_case)"
        hint="Dùng làm key trong JSON khi submit. Không đổi sau khi publish."
      >
        <TextInput
          value={field.name}
          onChange={(v) =>
            onUpdate({ name: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
          }
        />
      </Row>
      <Row label="Label hiển thị">
        <TextInput
          value={field.label}
          onChange={(v) => onUpdate({ label: v })}
        />
      </Row>
      <Row label="Placeholder">
        <TextInput
          value={field.placeholder ?? ""}
          onChange={(v) => onUpdate({ placeholder: v || null })}
        />
      </Row>
      <Row label="Help text">
        <TextInput
          value={field.help_text ?? ""}
          onChange={(v) => onUpdate({ help_text: v || null })}
        />
      </Row>
      <CheckboxInput
        label="Bắt buộc nhập"
        checked={field.required ?? false}
        onChange={(v) => onUpdate({ required: v })}
      />
    </Section>
  );
}
